/**
 * Mask Edit Branch Workflow for Paper2Ink.
 * Guarantees that re-vectorization after mask edits NEVER overwrites,
 * wipes, or silently resets an edited document.
 * Creates explicit new branches when manual edits exist.
 * Spec reference: .hermes/plans/2026-09-16_195645-paper2ink-saas-spec.md (§5.4, §6.1, §15 SAAS-215).
 */

import { validateCIMDocument, type CIMDocument, type UserDeclaration } from './model.ts';
import { CommandHistory } from './history.ts';
import type { Command } from './commands.ts';

export type BranchKind = 'main' | 'conversion' | 'mask_edit' | 'manual' | 'conflict';

export interface DocumentBranch {
  id: string;
  name: string;
  kind: BranchKind;
  documentId: string;
  baseRevisionId: string;
  tipRevisionId: string;
  createdAt: string;
  archivedAt?: string | null;
}

export interface DocumentRevision {
  id: string;
  branchId: string;
  parentRevisionId: string | null;
  document: CIMDocument;
  createdAt: string;
  description: string;
}

export interface RevectorizationRequest {
  newDocument: CIMDocument;
  sourceMaskHash?: string;
  description?: string;
}

export interface RevectorizationResult {
  branchCreated: boolean;
  activeBranchId: string;
  activeRevisionId: string;
  preservedBranchId?: string;
  activeDocument: CIMDocument;
  message: string;
}

export class BranchWorkflowManager {
  private readonly documentId: string;
  private readonly branches: Map<string, DocumentBranch> = new Map();
  private readonly revisions: Map<string, DocumentRevision> = new Map();
  private readonly branchHistories: Map<string, CommandHistory> = new Map();
  private readonly dirtyFlags: Map<string, boolean> = new Map();
  private activeBranchId: string;
  private idCounter = 1;

  constructor(initialDocument: CIMDocument, maxHistoryCapacity: number = 100, isDirty: boolean = false) {
    validateCIMDocument(initialDocument);
    this.documentId = initialDocument.document_id;

    const initialBranchId = `branch_main_${Date.now()}`;
    const initialRevisionId = `rev_1_${Date.now()}`;
    const now = new Date().toISOString();

    const initialRevision: DocumentRevision = {
      id: initialRevisionId,
      branchId: initialBranchId,
      parentRevisionId: null,
      document: structuredClone(initialDocument),
      createdAt: now,
      description: 'Initial document conversion/import',
    };
    this.revisions.set(initialRevisionId, initialRevision);

    const mainBranch: DocumentBranch = {
      id: initialBranchId,
      name: 'main',
      kind: 'main',
      documentId: this.documentId,
      baseRevisionId: initialRevisionId,
      tipRevisionId: initialRevisionId,
      createdAt: now,
    };
    this.branches.set(initialBranchId, mainBranch);
    this.branchHistories.set(initialBranchId, new CommandHistory(maxHistoryCapacity));
    this.dirtyFlags.set(initialBranchId, isDirty);

    this.activeBranchId = initialBranchId;
  }

  createBranch(name: string, kind: BranchKind = 'manual'): DocumentBranch {
    const currentBranch = this.activeBranch;
    const newBranchId = this.nextId('branch');
    const now = new Date().toISOString();
    const newBranch: DocumentBranch = {
      id: newBranchId,
      name,
      kind,
      documentId: this.documentId,
      baseRevisionId: currentBranch.tipRevisionId,
      tipRevisionId: currentBranch.tipRevisionId,
      createdAt: now,
    };
    this.branches.set(newBranchId, newBranch);
    this.branchHistories.set(newBranchId, new CommandHistory());
    this.dirtyFlags.set(newBranchId, false);
    return newBranch;
  }

  markDirty(branchId?: string): void {
    this.dirtyFlags.set(branchId ?? this.activeBranchId, true);
  }

  private nextId(prefix: string): string {
    return `${prefix}_${this.idCounter++}_${Date.now().toString(36)}`;
  }

  get activeBranch(): DocumentBranch {
    const branch = this.branches.get(this.activeBranchId);
    if (!branch) {
      throw new Error(`Active branch ${this.activeBranchId} not found`);
    }
    return branch;
  }

  getActiveBranch(): DocumentBranch {
    return this.activeBranch;
  }

  getBranch(branchId: string): DocumentBranch | undefined {
    return this.branches.get(branchId);
  }

  listBranches(): DocumentBranch[] {
    return Array.from(this.branches.values());
  }

  getActiveRevision(): DocumentRevision {
    const branch = this.activeBranch;
    const revision = this.revisions.get(branch.tipRevisionId);
    if (!revision) {
      throw new Error(`Tip revision ${branch.tipRevisionId} not found for branch ${branch.id}`);
    }
    return revision;
  }

  getActiveDocument(): CIMDocument {
    return this.getActiveRevision().document;
  }

  hasManualEdits(branchId?: string): boolean {
    const bid = branchId ?? this.activeBranchId;
    const isDirty = this.dirtyFlags.get(bid) ?? false;
    const history = this.branchHistories.get(bid);
    const hasHistoryEdits = history ? history.canUndo : false;
    return isDirty || hasHistoryEdits;
  }

  applyCommand(command: Command): CIMDocument {
    const currentRev = this.getActiveRevision();
    let history = this.branchHistories.get(this.activeBranchId);
    if (!history) {
      history = new CommandHistory();
      this.branchHistories.set(this.activeBranchId, history);
    }

    const updatedDoc = history.execute(command, currentRev.document);
    // User edits invalidate existing user review declaration
    if (updatedDoc.review) {
      updatedDoc.review.user_declaration = null;
    }

    currentRev.document = updatedDoc;
    this.dirtyFlags.set(this.activeBranchId, true);
    return updatedDoc;
  }

  undo(): CIMDocument {
    const currentRev = this.getActiveRevision();
    const history = this.branchHistories.get(this.activeBranchId);
    if (!history || !history.canUndo) {
      return currentRev.document;
    }
    const undoneDoc = history.undo(currentRev.document);
    if (undoneDoc.review) {
      undoneDoc.review.user_declaration = null;
    }
    currentRev.document = undoneDoc;
    return undoneDoc;
  }

  redo(): CIMDocument {
    const currentRev = this.getActiveRevision();
    const history = this.branchHistories.get(this.activeBranchId);
    if (!history || !history.canRedo) {
      return currentRev.document;
    }
    const redoneDoc = history.redo(currentRev.document);
    if (redoneDoc.review) {
      redoneDoc.review.user_declaration = null;
    }
    currentRev.document = redoneDoc;
    return redoneDoc;
  }

  getActiveHistory(): CommandHistory | undefined {
    return this.branchHistories.get(this.activeBranchId);
  }

  setActiveDocumentDeclaration(declaration: UserDeclaration): void {
    const currentRev = this.getActiveRevision();
    currentRev.document = {
      ...currentRev.document,
      review: {
        ...currentRev.document.review,
        user_declaration: declaration,
      },
    };
  }

  switchBranch(branchId: string): CIMDocument {
    const branch = this.branches.get(branchId);
    if (!branch) {
      throw new Error(`Branch with ID ${branchId} does not exist`);
    }
    this.activeBranchId = branchId;
    return this.getActiveDocument();
  }

  /**
   * Performs re-vectorization from a new mask.
   * Invariant (§5.4, §6.1, §15):
   * If manual edits exist on the active branch, existing edits are strictly preserved,
   * and a new branch is created without overwriting or silently wiping user edits.
   */
  revectorize(request: RevectorizationRequest): RevectorizationResult {
    validateCIMDocument(request.newDocument);

    // Prepare fresh re-vectorized document with clean declaration state
    const cleanRevecDoc: CIMDocument = structuredClone(request.newDocument);
    if (cleanRevecDoc.review) {
      cleanRevecDoc.review.user_declaration = null;
    }

    const currentBranch = this.activeBranch;
    const now = new Date().toISOString();
    const desc = request.description ?? 'Re-vectorization from updated mask';

    if (!this.hasManualEdits(this.activeBranchId)) {
      // Clean document: advance head on current branch
      const newRevId = this.nextId('rev');
      const newRevision: DocumentRevision = {
        id: newRevId,
        branchId: currentBranch.id,
        parentRevisionId: currentBranch.tipRevisionId,
        document: cleanRevecDoc,
        createdAt: now,
        description: desc,
      };
      this.revisions.set(newRevId, newRevision);
      currentBranch.tipRevisionId = newRevId;

      return {
        branchCreated: false,
        activeBranchId: currentBranch.id,
        activeRevisionId: newRevId,
        activeDocument: cleanRevecDoc,
        message: 'Re-vectorization applied directly to unmodified branch head.',
      };
    }

    // Manual edits detected: DO NOT overwrite or wipe!
    // Create an explicit new branch and preserve current branch intact.
    const newBranchId = this.nextId('branch_mask_edit');
    const newRevId = this.nextId('rev_mask_edit');
    const branchIndex = this.listBranches().filter((b) => b.kind === 'mask_edit').length + 1;
    const branchName = `mask_edit_${branchIndex}`;

    const newRevision: DocumentRevision = {
      id: newRevId,
      branchId: newBranchId,
      parentRevisionId: currentBranch.baseRevisionId,
      document: cleanRevecDoc,
      createdAt: now,
      description: `${desc} (detached from ${currentBranch.name} to preserve manual edits)`,
    };
    this.revisions.set(newRevId, newRevision);

    const newBranch: DocumentBranch = {
      id: newBranchId,
      name: branchName,
      kind: 'mask_edit',
      documentId: this.documentId,
      baseRevisionId: currentBranch.tipRevisionId,
      tipRevisionId: newRevId,
      createdAt: now,
    };
    this.branches.set(newBranchId, newBranch);
    this.branchHistories.set(newBranchId, new CommandHistory());
    this.dirtyFlags.set(newBranchId, false);

    const preservedBranchId = currentBranch.id;
    this.activeBranchId = newBranchId;

    return {
      branchCreated: true,
      activeBranchId: newBranchId,
      activeRevisionId: newRevId,
      preservedBranchId,
      activeDocument: cleanRevecDoc,
      message: `Manual edits detected on '${currentBranch.name}'. Preserved original branch and created new branch '${branchName}' without overwriting edits.`,
    };
  }

  /**
   * Applies an asynchronous conversion job result (§8, SAAS-410).
   * If isDetachedBranch is true or manual edits exist on the current branch,
   * creates an explicit 'conversion' branch preserving user edits.
   * Otherwise advances current branch tip directly.
   */
  applyConversionResult(
    cimDocument: CIMDocument,
    revisionId: string,
    isDetachedBranch: boolean,
    serverBranchId?: string
  ): { branch: DocumentBranch; revisionId: string; document: CIMDocument } {
    validateCIMDocument(cimDocument);
    const now = new Date().toISOString();
    const currentBranch = this.activeBranch;

    if (isDetachedBranch || this.hasManualEdits(currentBranch.id)) {
      const branchId = serverBranchId || this.nextId('branch_conv');
      const branchIndex = this.listBranches().filter((b) => b.kind === 'conversion').length + 1;
      const branchName = `conversion_${branchIndex}`;

      const newRevision: DocumentRevision = {
        id: revisionId,
        branchId,
        parentRevisionId: currentBranch.baseRevisionId,
        document: structuredClone(cimDocument),
        createdAt: now,
        description: 'Conversion result (detached to preserve user manual edits)',
      };
      this.revisions.set(revisionId, newRevision);

      const newBranch: DocumentBranch = {
        id: branchId,
        name: branchName,
        kind: 'conversion',
        documentId: this.documentId,
        baseRevisionId: currentBranch.tipRevisionId,
        tipRevisionId: revisionId,
        createdAt: now,
      };
      this.branches.set(branchId, newBranch);
      this.branchHistories.set(branchId, new CommandHistory());
      this.dirtyFlags.set(branchId, false);
      this.activeBranchId = branchId;

      return { branch: newBranch, revisionId, document: newRevision.document };
    } else {
      // Direct head advance
      const newRevision: DocumentRevision = {
        id: revisionId,
        branchId: currentBranch.id,
        parentRevisionId: currentBranch.tipRevisionId,
        document: structuredClone(cimDocument),
        createdAt: now,
        description: 'Conversion result applied to branch head',
      };
      this.revisions.set(revisionId, newRevision);
      currentBranch.tipRevisionId = revisionId;
      this.dirtyFlags.set(currentBranch.id, false);

      return { branch: currentBranch, revisionId, document: newRevision.document };
    }
  }

  /**
   * Directly record a revision onto a branch and advance its tip (§6.1, SAAS-411).
   */
  addRevision(
    branchId: string,
    revisionId: string,
    document: CIMDocument,
    description: string = 'Revision update',
    parentRevisionId?: string | null
  ): DocumentRevision {
    validateCIMDocument(document);
    const branch = this.branches.get(branchId);
    if (!branch) {
      throw new Error(`Branch with ID ${branchId} does not exist`);
    }

    const now = new Date().toISOString();
    const revision: DocumentRevision = {
      id: revisionId,
      branchId,
      parentRevisionId: parentRevisionId !== undefined ? parentRevisionId : branch.tipRevisionId,
      document: structuredClone(document),
      createdAt: now,
      description,
    };

    this.revisions.set(revisionId, revision);
    branch.tipRevisionId = revisionId;
    this.dirtyFlags.set(branchId, false);
    return revision;
  }

  /**
   * Creates an explicit conflict branch containing local edits (§6.1, SAAS-411).
   * Preserves current branch state and switches to the new conflict branch.
   */
  createConflictBranch(
    branchName?: string,
    customRevisionId?: string
  ): { branch: DocumentBranch; revision: DocumentRevision; document: CIMDocument } {
    const currentBranch = this.activeBranch;
    const currentDoc = this.getActiveDocument();
    const now = new Date().toISOString();

    const conflictIndex = this.listBranches().filter((b) => b.kind === 'conflict').length + 1;
    const name = branchName || `conflict_${conflictIndex}`;
    const branchId = this.nextId('branch_conflict');
    const revId = customRevisionId || this.nextId('rev_conflict');

    const newBranch: DocumentBranch = {
      id: branchId,
      name,
      kind: 'conflict',
      documentId: this.documentId,
      baseRevisionId: currentBranch.baseRevisionId,
      tipRevisionId: revId,
      createdAt: now,
    };
    this.branches.set(branchId, newBranch);
    this.branchHistories.set(branchId, new CommandHistory());
    this.dirtyFlags.set(branchId, false);

    const revision: DocumentRevision = {
      id: revId,
      branchId,
      parentRevisionId: currentBranch.baseRevisionId,
      document: structuredClone(currentDoc),
      createdAt: now,
      description: `Conflict fork from ${currentBranch.name}`,
    };
    this.revisions.set(revId, revision);

    this.activeBranchId = branchId;
    return { branch: newBranch, revision, document: revision.document };
  }

  /**
   * Replaces the active document on current branch head with an updated snapshot (e.g. pull from server).
   */
  replaceActiveDocument(newDoc: CIMDocument, newRevisionId?: string): CIMDocument {
    validateCIMDocument(newDoc);
    const currentBranch = this.activeBranch;
    const revId = newRevisionId || this.nextId('rev_pull');
    const now = new Date().toISOString();

    const newRevision: DocumentRevision = {
      id: revId,
      branchId: currentBranch.id,
      parentRevisionId: currentBranch.tipRevisionId,
      document: structuredClone(newDoc),
      createdAt: now,
      description: 'Synchronized from server head',
    };
    this.revisions.set(revId, newRevision);
    currentBranch.tipRevisionId = revId;
    this.dirtyFlags.set(currentBranch.id, false);
    return newRevision.document;
  }
}
